import type { PouleDetail, PouleInfoResponse } from '@/features/poules/api'
import { useCreatePouleInvite } from '@/features/poules/api'
import { useTranslation } from '@/i18n'
import { shareOrCopy } from '@/lib/share'
import { Link } from 'react-router-dom'
import { useState } from 'react'

export function PouleDetailHero({
  poule,
  memberCount,
  pouleId,
  canInvite = false,
  canManage = false,
  canManageLicense = false,
  licenseActionLabel,
}: {
  poule: PouleDetail
  memberCount?: PouleInfoResponse['member_count']
  pouleId?: number | null
  canInvite?: boolean
  canManage?: boolean
  canManageLicense?: boolean
  licenseActionLabel?: string
}) {
  const { t } = useTranslation()
  const [shareState, setShareState] = useState<'idle' | 'success' | 'error'>('idle')
  const createInvite = useCreatePouleInvite(canInvite ? (pouleId ?? null) : null)
  const mediaUrl =
    normalizeOptionalString(poule.hero_url) ??
    normalizeOptionalString(poule.profile_url) ??
    normalizeOptionalString(poule.thumbnail_url)

  return (
    <section className="overflow-hidden rounded-[1.25rem] border border-white/[0.06] bg-[#0d1113]">
      <div className="flex items-center gap-3.5 p-4 sm:gap-5 sm:p-5">
        {mediaUrl ? (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center sm:h-20 sm:w-20">
            <img
              src={mediaUrl}
              alt=""
              className="h-full w-full object-contain"
            />
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-black tracking-tight text-white sm:text-2xl">
            {poule.name}
          </h1>
          {typeof memberCount === 'number' ? (
            <p className="mt-1.5 text-xs font-semibold text-surface-500">
              {t('{count} leden', { count: memberCount })}
            </p>
          ) : null}
          {shareState === 'error' ? <p className="mt-1 text-xs text-red-300">{t('Delen is momenteel niet beschikbaar.')}</p> : null}
          {shareState === 'success' ? <p className="mt-1 text-xs text-emerald-300">{t('Invite-link gedeeld of gekopieerd.')}</p> : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {canManageLicense ? (
            <Link
              to={`/poules/${poule.id}/licentie`}
              className="inline-flex h-10 items-center rounded-xl border border-white/[0.12] px-3 text-xs font-bold text-surface-200 transition hover:bg-white/[0.07]"
            >
              {licenseActionLabel ?? t('Upgrade nu')}
            </Link>
          ) : null}

          {canManage ? (
            <Link
              to={`/dashboard/poules/${poule.id}/beheer`}
              className="inline-flex h-10 items-center rounded-xl border border-white/[0.12] px-3 text-xs font-bold text-surface-200 transition hover:bg-white/[0.07]"
            >
              {t('Beheren')}
            </Link>
          ) : null}

          {canInvite ? (
            <button
              type="button"
              disabled={createInvite.isPending}
              aria-label={createInvite.isPending ? t('Link maken...') : t('Iemand uitnodigen')}
              title={createInvite.isPending ? t('Link maken...') : t('Iemand uitnodigen')}
              onClick={() => {
                if (createInvite.isPending) return
                setShareState('idle')
                void createInvite.mutateAsync().then(async (invite) => {
                  await shareOrCopy({
                    title: poule.name,
                    text: t('Doe mee met de poule "{name}" via deze invite-link:\n{url}', {
                      name: poule.name,
                      url: invite.inviteUrl,
                    }),
                    url: invite.inviteUrl,
                  })
                  setShareState('success')
                }).catch((error: unknown) => {
                  if (error instanceof Error && error.name === 'AbortError') return
                  setShareState('error')
                })
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent-600 text-white shadow-lg shadow-accent-950/20 transition hover:bg-accent-500 disabled:cursor-wait disabled:opacity-60"
            >
              <ShareIcon />
            </button>
          ) : null}

          {poule.has_prizes ? (
            <MetaPill
              label={
                typeof poule.prizes_count === 'number' && poule.prizes_count > 0
                  ? `${poule.prizes_count} prijzen`
                  : 'Prijzen actief'
              }
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}

function MetaPill({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-[#202427] bg-[#101416] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-surface-300">
      {label}
    </span>
  )
}

function ShareIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="18" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="6" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="18" cy="19" r="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

function normalizeOptionalString(value?: string | null) {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}
